import type { GridValidRowModel } from "@mui/x-data-grid";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { FacetSpec } from "../../../components/UIToolkit/Filters/facetSections";
import type { SystemGridColDef } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import type { SharedFacetGroup } from "../../../hooks/useFacetFilters";
import { boldNameColumn } from "../pbjGridCells";
import type { PbjSortColumn } from "../pbjGridSorting";
import type { WithBuilding } from "./buildingQueries";
import { usePbjNetwork } from "./PbjNetworkContext";

/** The Building facet's id — also the "Filter by…" target of the Building column. */
export const BUILDING_FACET = "building";
export const BUILDING_FIELD = "buildingName" satisfies keyof WithBuilding<object>;

/** A grid row that carries its building (see `WithBuilding`). */
type BuildingGridRow = GridValidRowModel & WithBuilding<object>;

export type BuildingGridParts<GridRow extends BuildingGridRow> = {
	/** The Building column, first in the grid, for a supervisor of two or more buildings; otherwise none. */
	columns: SystemGridColDef<GridRow>[];
	/** The Building facet group, first in the rail, for a supervisor of two or more buildings; otherwise none. */
	facets: FacetSpec<GridRow>[];
	/** The Building facet's selection, shared through the URL by every PBJ grid. */
	sharedGroups: readonly SharedFacetGroup[];
	/** The Building sort option, for a supervisor of two or more buildings; otherwise none. */
	sortFields: PbjSortColumn<GridRow>[];
	/** Only the header department's rows can be edited; other buildings are read-only here. */
	canEditRow: (row: GridRow) => boolean;
};

/** Building column, facet and shared selection for a PBJ grid whose rows carry their building. */
export function useBuildingGridParts<GridRow extends BuildingGridRow>(): BuildingGridParts<GridRow> {
	const { t } = useTranslation();
	const { isNetwork, buildings, buildingName, homeDepartmentId, selectedBuildingIds, setSelectedBuildingIds } = usePbjNetwork();

	const columns = useMemo<SystemGridColDef<GridRow>[]>(
		() => (isNetwork ? [boldNameColumn<GridRow>(BUILDING_FIELD, t("pbj_model.network.building"), { minWidth: 160 })] : []),
		[isNetwork, t],
	);

	const facets = useMemo<FacetSpec<GridRow>[]>(
		() =>
			isNetwork
				? [
						{
							dimension: { id: BUILDING_FACET, getKeys: (row) => [row.buildingId] },
							title: t("pbj_model.network.building"),
							getLabel: buildingName,
							// Every building stays listed, even one with no rows here, so the filter reads the same on every tab.
							order: buildings.map((building) => building.departmentId),
						},
					]
				: [],
		[isNetwork, t, buildingName, buildings],
	);

	const sharedGroups = useMemo<readonly SharedFacetGroup[]>(
		() => (isNetwork ? [{ id: BUILDING_FACET, selected: selectedBuildingIds, onChange: setSelectedBuildingIds }] : []),
		[isNetwork, selectedBuildingIds, setSelectedBuildingIds],
	);

	const sortFields = useMemo<PbjSortColumn<GridRow>[]>(
		() => (isNetwork ? [{ field: BUILDING_FIELD, label: t("pbj_model.network.building") }] : []),
		[isNetwork, t],
	);

	const canEditRow = useCallback((row: GridRow) => row.buildingId === homeDepartmentId, [homeDepartmentId]);

	return { columns, facets, sharedGroups, sortFields, canEditRow };
}

/**
 * A grid's column → facet map, with the Building column mapped to the Building facet for a network — the
 * only time the Building group is in the rail, so "Filter by…" never points at a missing group.
 * Pass a module-level constant so the result stays stable.
 */
export function useWithBuildingFacet(columnFacet: Readonly<Record<string, string>>): Readonly<Record<string, string>> {
	const { isNetwork } = usePbjNetwork();
	return useMemo(() => (isNetwork ? { ...columnFacet, [BUILDING_FIELD]: BUILDING_FACET } : columnFacet), [isNetwork, columnFacet]);
}
